import { useMemo, useState } from 'react';
import { Flex, Image, Input, Select, Table, Tag, Typography } from 'antd';
import { formatBytes, REFERENCE_LABEL, type ImageObjectRow, type Reference } from '../../lib/ops-types';

const REF_COLOR: Record<Reference, string> = { catalog: 'blue', search: 'green', both: 'purple', none: 'default' };

export const folderOf = (key: string) => key.split('/').slice(0, -1).join('/') + '/';

export function filterImages(rows: ImageObjectRow[], f: { text: string; folder: string | null; reference: Reference | null }) {
  const t = f.text.trim().toLowerCase();
  return rows.filter((r) => (!f.folder || folderOf(r.key) === f.folder) && (!f.reference || r.reference === f.reference) && (!t || r.key.toLowerCase().includes(t)));
}

export function ImagesTab({ rows }: { rows: ImageObjectRow[] }) {
  const [text, setText] = useState('');
  const [folder, setFolder] = useState<string | null>(null);
  const [reference, setReference] = useState<Reference | null>(null);
  const folders = useMemo(() => [...new Set(rows.map((r) => folderOf(r.key)))].sort(), [rows]);
  const data = useMemo(() => filterImages(rows, { text, folder, reference }), [rows, text, folder, reference]);

  return (
    <>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <Input.Search allowClear placeholder="파일 경로" style={{ width: 240 }} onChange={(e) => setText(e.target.value)} />
        <Select allowClear placeholder="폴더" style={{ width: 220 }} value={folder} onChange={(v) => setFolder(v ?? null)} options={folders.map((f) => ({ value: f, label: f }))} />
        <Select
          allowClear
          placeholder="참조"
          style={{ width: 140 }}
          value={reference}
          onChange={(v) => setReference(v ?? null)}
          options={(Object.keys(REFERENCE_LABEL) as Reference[]).map((r) => ({ value: r, label: REFERENCE_LABEL[r] }))}
        />
        <Typography.Text type="secondary">{data.length} / {rows.length}개</Typography.Text>
      </Flex>
      <Table
        size="small"
        rowKey="key"
        dataSource={data}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: 800 }}
        columns={[
          { title: '이미지', dataIndex: 'url', width: 72, render: (u: string, r) => <Image src={u} width={48} height={48} style={{ objectFit: 'contain' }} alt={r.key} /> },
          { title: '경로', dataIndex: 'key', render: (k: string) => <Typography.Text copyable code>{k}</Typography.Text> },
          { title: '크기', dataIndex: 'size', width: 90, align: 'right', render: formatBytes },
          { title: '참조', dataIndex: 'reference', width: 110, render: (r: Reference) => <Tag color={REF_COLOR[r]}>{REFERENCE_LABEL[r]}</Tag> },
          { title: '검색 문서', dataIndex: 'searchDocIds', width: 220, render: (ids: string[]) => (ids.length ? ids.join(', ') : '-') },
        ]}
      />
    </>
  );
}
